import { OUTREACH_RAMP, mailboxDailyCap } from 'fym-shared/src/music24/outreach/mailboxRamp';

const DAY_MS = 24 * 60 * 60 * 1000;
/** No provider cap in the plan: the plan rate and the ramp decide. */
const NO_PROVIDER_CAP = 1000;

export interface FirstWeekDay {
  /** Midnight UTC of the day. */
  day: Date;
  /** The new pitches of the day: the plan rate, at most the warm-up cap of the mailbox. */
  pitches: number;
  /** The follow-ups of pitches sent `followUpDays` days before. They do not count. */
  followUps: number;
  /** A weekend day with "weekdays only": no pitches. */
  off: boolean;
  /** The warm-up holds the day below the plan rate. */
  warmup: boolean;
  isRelease: boolean;
}

function isWeekend(time: number): boolean {
  const weekday = new Date(time).getUTCDay();
  return weekday === 0 || weekday === 6;
}

/**
 * The first 7 days of pitches (design outreach-v2 c1 "Your first week"), from tomorrow. A day
 * sends the plan rate, but a mailbox in the warm-up sends at most its cap of that day. With no
 * mailbox yet, the ramp of a new mailbox (connected today) applies. Pure.
 */
export function firstWeekPlan(input: {
  now: Date;
  rate: number;
  weekdaysOnly: boolean;
  followUpDays: number | null;
  releaseDate: string | null;
  /** The connect time of the mailbox of the campaign; null: a new mailbox. */
  mailboxConnectedAt: Date | null;
}): FirstWeekDay[] {
  const today = Math.floor(input.now.getTime() / DAY_MS) * DAY_MS;
  const connectedAt = input.mailboxConnectedAt ?? new Date(today);
  const release = input.releaseDate === null ? null : Math.floor(Date.parse(input.releaseDate) / DAY_MS) * DAY_MS;
  const sentOn = new Map<number, number>();
  const days: FirstWeekDay[] = [];
  for (let index = 1; index <= 7; index += 1) {
    const time = today + index * DAY_MS;
    const off = input.weekdaysOnly && isWeekend(time);
    const cap = mailboxDailyCap({
      connectedAt,
      now: new Date(time + 12 * 60 * 60 * 1000),
      seasoned: false,
      resumedAt: null,
      providerCap: NO_PROVIDER_CAP,
    });
    const pitches = off ? 0 : Math.min(input.rate, cap);
    sentOn.set(time, pitches);
    const followUps = off || input.followUpDays === null ? 0 : (sentOn.get(time - input.followUpDays * DAY_MS) ?? 0);
    days.push({
      day: new Date(time),
      pitches,
      followUps,
      off,
      warmup: !off && cap < input.rate,
      isRelease: release === time,
    });
  }
  return days;
}

/** The day when the warm-up of a mailbox reaches `rate` pitches a day, or null when it never does. */
export function rampReachesOn(connectedAt: Date, rate: number): Date | null {
  const step = OUTREACH_RAMP.find((item) => item.perDay >= rate);
  return step === undefined ? null : new Date(connectedAt.getTime() + step.fromDay * DAY_MS);
}
